<template>
  <div part="base" class="sp-canonical-update-modal">
    <sp-modal
      :modal-title="store.config.modalTitle"
      width="44rem"
      :open="store.isOpen"
      :closeable.prop="modalCloseable"
      :persistent.prop="store.isSubmitting"
      :no-scrollable-content.prop.camel="previewOwnsScrolling"
      @update-open="handleUpdateOpen"
      @close="handleClose"
    >
      <sp-card-content-overlay slot="content-overlay" :visible="store.isSubmitting">
        <sp-progress type="ellipsis" size="x-small"></sp-progress>
      </sp-card-content-overlay>

      <PreviewProgressPanel
        v-if="store.isLoading"
        :loading-text="store.labels.loading"
        :percent="store.progressPercent"
        :progress-text="store.progressText"
        :remaining-text="store.remainingText"
        :test-id="TestId.canonicalUpdate.progress"
      />
      <CanonicalUpdateReviewStep v-else />

      <CanonicalUpdateModalActions slot="actions" />
    </sp-modal>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { TestId } from "#/test-ids";
import { useExpose } from "#ce/composables/useExpose";
import PreviewProgressPanel from "#ce/apps/SpAdmin/shared/components/PreviewProgressPanel.ce.vue";
import CanonicalUpdateReviewStep from "./components/CanonicalUpdateReviewStep.ce.vue";
import CanonicalUpdateModalActions from "./components/CanonicalUpdateModalActions.ce.vue";
import { props as appProps } from "./props.js";
import { useCanonicalUpdateStore } from "./store/canonical-update";

const props = defineProps(appProps);

const store = useCanonicalUpdateStore();
const { exposeMethods } = useExpose();

// Only the final write locks the modal. The preview comparison can take a while, so
// the modal stays dismissible: the comparison keeps running on the server, and
// reopening the modal resumes it.
const modalCloseable = computed(() => !store.isSubmitting);
// A ready preview with rows owns scrolling inside its table. Loading,
// empty, and error-only states leave scrolling to the modal body.
const previewOwnsScrolling = computed(() => !store.isLoading && store.rows.length > 0);

function buildConfig(overrides = {}) {
  return {
    previewUrl: props.previewUrl,
    statusUrl: props.statusUrl,
    processUrl: props.processUrl,
    modalTitle: props.modalTitle,
    statusLabels: props.statusLabels,
    labels: props.labels,
    ...overrides,
  };
}

function open(overrides) {
  store.configure(buildConfig(overrides));
  store.open();
}

function close() {
  store.close();
}

exposeMethods({ open, close });

onMounted(() => {
  store.configure(buildConfig());
});

function handleUpdateOpen({ detail }) {
  const [value] = detail;

  if (value) {
    // sp-modal re-emits update-open(true) for an open the store initiated;
    // opening again would restart the preview fetch.
    if (!store.isOpen) {
      store.open();
    }
    return;
  }

  handleClose();
}

function handleClose() {
  // Ignore dismissal only while the apply request is in flight.
  if (store.isSubmitting) {
    return;
  }

  close();
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped>
.sp-canonical-update-modal {
  display: block;

  --sp-comp-card-padding-block: 1rem;
  --sp-card-actions-padding-inline: 0;
  --sp-card-actions-padding-block: 0;
}
</style>
